// Global Instructions Rule Applied!
//
// Configuration → Chart Templates: named, reusable chart-of-accounts
// definitions (an account subset + an item -> account map) that a new fund can
// be created from.
//
// A template is a SNAPSHOT, not a live link: editing one never reaches back
// into the funds already created from it. Deleting one likewise leaves those
// funds alone.
//
// Master-detail, following IndustriesPanel: the list is replaced by the editor
// when a template is opened.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Checkbox, Input, Popconfirm, Select, Table, Typography } from 'antd'
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type {
  ChartTemplate,
  GlAccountAdminRow,
  GlItemDefaultRow
} from '../../../state_manager/data_models/fund_chart_of_accounts.ts'
import {
  fetchChartTemplates,
  saveChartTemplate,
  deleteChartTemplate,
  fetchGlAccountAdmin
} from '../services/accountingConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Title, Text } = Typography

interface EditorState {
  id: string | null
  name: string
  description: string
  accountIds: Set<string>
  itemMap: Record<string, string>
}

const blankEditor = (): EditorState => ({
  id: null,
  name: '',
  description: '',
  accountIds: new Set<string>(),
  itemMap: {}
})

export default function ChartTemplatesPanel() {
  const [templates, setTemplates] = useState<ChartTemplate[]>([])
  const [catalogue, setCatalogue] = useState<GlAccountAdminRow[]>([])
  const [items, setItems] = useState<GlItemDefaultRow[]>([])
  const [loading, setLoading] = useState(false)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [tpl, admin] = await Promise.all([fetchChartTemplates(token()), fetchGlAccountAdmin(token())])
      if (tpl.error) showErrorToast(tpl.error)
      if (admin.error) showErrorToast(admin.error)
      setTemplates(tpl.data)
      setCatalogue(admin.accounts)
      setItems(admin.items)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const openEditor = useCallback((tpl: ChartTemplate | null) => {
    if (!tpl) {
      setEditor(blankEditor())
      return
    }
    setEditor({
      id: tpl.cfg_acc_chart_templates__id,
      name: tpl.cfg_acc_chart_templates__name,
      description: tpl.cfg_acc_chart_templates__description ?? '',
      accountIds: new Set(tpl.prog__accounts.map((a) => a.cfg_acc_gl_accounts__id)),
      itemMap: Object.fromEntries(
        tpl.prog__items.map((i) => [i.cfg_acc_items__id, i.cfg_acc_gl_accounts__id])
      )
    })
  }, [])

  const handleDelete = useCallback(
    async (templateId: string) => {
      setDeletingId(templateId)
      try {
        const res = await deleteChartTemplate(templateId, token())
        if (!res.success) {
          showErrorToast(res.error || 'Failed to delete the template')
          return
        }
        toast.success(res.data?.status_message || 'Template deleted')
        await load()
      } finally {
        setDeletingId(null)
      }
    },
    [load]
  )

  const handleSave = useCallback(async () => {
    if (!editor) return
    if (!editor.name.trim()) {
      showErrorToast('Template name is required')
      return
    }
    // mirror the server rule so the user is told before the round-trip
    const outside = Object.entries(editor.itemMap).filter(
      ([, glId]) => glId && !editor.accountIds.has(glId)
    )
    if (outside.length > 0) {
      showErrorToast('Some accounting items map to accounts that are not in the template — add those accounts or clear the mapping')
      return
    }

    setSaving(true)
    try {
      const res = await saveChartTemplate(
        {
          ...(editor.id ? { id: editor.id } : {}),
          name: editor.name.trim(),
          description: editor.description.trim() || null,
          accounts: Array.from(editor.accountIds),
          item_maps: Object.entries(editor.itemMap)
            .filter(([, glId]) => Boolean(glId))
            .map(([itemId, glId]) => ({ account_item_id: itemId, gl_account_id: glId }))
        },
        token()
      )
      if (!res.success) {
        // name collisions and membership violations come back as 422 - keep the
        // editor open with the user's work intact
        showErrorToast(res.error || 'Failed to save the template')
        return
      }
      toast.success(res.data?.status_message || 'Template saved')
      setEditor(null)
      await load()
    } finally {
      setSaving(false)
    }
  }, [editor, load])

  const listColumns: ColumnsType<ChartTemplate> = [
    { title: 'Template', dataIndex: 'cfg_acc_chart_templates__name' },
    {
      title: 'Source fund',
      dataIndex: 'funds__name',
      width: 180,
      render: (v: string | null) => v ?? <Text type='secondary'>—</Text>
    },
    { title: 'Accounts', dataIndex: 'prog__account_count', width: 100, align: 'right' },
    { title: 'Item mappings', dataIndex: 'prog__item_count', width: 130, align: 'right' },
    {
      title: '',
      width: 100,
      align: 'center',
      render: (_: unknown, row) => (
        <div className='flex items-center justify-center gap-1'>
          <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openEditor(row)} />
          <Popconfirm
            title='Delete this template?'
            description='Funds already created from it are unaffected.'
            okText='Delete'
            okButtonProps={{ danger: true, loading: deletingId === row.cfg_acc_chart_templates__id }}
            onConfirm={() => void handleDelete(row.cfg_acc_chart_templates__id)}
          >
            <Button type='text' size='small' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      )
    }
  ]

  const accountColumns: ColumnsType<GlAccountAdminRow> = [
    {
      title: 'In template',
      width: 100,
      align: 'center',
      render: (_: unknown, row) => (
        <Checkbox
          checked={editor?.accountIds.has(row.cfg_acc_gl_accounts__id) ?? false}
          onChange={(e) =>
            setEditor((prev) => {
              if (!prev) return prev
              const next = new Set(prev.accountIds)
              if (e.target.checked) next.add(row.cfg_acc_gl_accounts__id)
              else next.delete(row.cfg_acc_gl_accounts__id)
              return { ...prev, accountIds: next }
            })
          }
        />
      )
    },
    { title: 'Code', dataIndex: 'acc_gl_accounts__account_code', width: 90, render: (v) => v ?? '—' },
    { title: 'Account', dataIndex: 'acc_gl_accounts__account_name' },
    { title: 'Type', dataIndex: 'cfg_acc_account_types__label', width: 120, render: (v) => v ?? '—' }
  ]

  // the item-map select is restricted to the checked account subset, so the
  // template can never reference an account it does not contain
  const templateAccountOptions = useMemo(
    () =>
      catalogue
        .filter((a) => editor?.accountIds.has(a.cfg_acc_gl_accounts__id))
        .map((a) => ({
          value: a.cfg_acc_gl_accounts__id,
          label: `${a.acc_gl_accounts__account_code ? `${a.acc_gl_accounts__account_code} · ` : ''}${a.acc_gl_accounts__account_name}`
        })),
    [catalogue, editor]
  )

  const itemColumns: ColumnsType<GlItemDefaultRow> = [
    { title: 'Accounting item', dataIndex: 'cfg_acc_items__label' },
    {
      title: 'GL account',
      width: 340,
      render: (_: unknown, row) => (
        <Select
          size='small'
          className='w-full'
          showSearch
          allowClear
          optionFilterProp='label'
          placeholder='Not mapped'
          value={editor?.itemMap[row.cfg_acc_items__id] ?? undefined}
          options={templateAccountOptions}
          onChange={(v) =>
            setEditor((prev) => {
              if (!prev) return prev
              const next = { ...prev.itemMap }
              if (v) next[row.cfg_acc_items__id] = v
              else delete next[row.cfg_acc_items__id]
              return { ...prev, itemMap: next }
            })
          }
        />
      )
    }
  ]

  if (editor) {
    return (
      <div className={CONFIG_PANEL_SCROLL_CLASS}>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <Button size='small' icon={<ArrowLeftOutlined />} onClick={() => setEditor(null)}>
              Back
            </Button>
            <Title level={5} style={{ margin: 0 }}>
              {editor.id ? 'Edit template' : 'New template'}
            </Title>
          </div>
          <Button
            type='primary'
            size='small'
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => void handleSave()}
          >
            Save Template
          </Button>
        </div>

        <div className='flex flex-col gap-3 mb-4' style={{ maxWidth: 520 }}>
          <div>
            <Text type='secondary'>Name</Text>
            <Input
              value={editor.name}
              maxLength={120}
              placeholder='Template name'
              onChange={(e) => setEditor((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
            />
          </div>
          <div>
            <Text type='secondary'>Description</Text>
            <Input
              value={editor.description}
              maxLength={250}
              placeholder='Optional'
              onChange={(e) => setEditor((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
            />
          </div>
        </div>

        <Title level={5}>Accounts</Title>
        <Table
          size='small'
          loading={loading}
          columns={accountColumns}
          dataSource={catalogue}
          pagination={false}
          rowKey='cfg_acc_gl_accounts__id'
        />

        <Title level={5} className='mt-6'>
          Item mapping
        </Title>
        <Text type='secondary' className='block mb-2'>
          Only accounts ticked above can be chosen. Items left unmapped fall back to the global
          default when the template is used.
        </Text>
        <Table
          size='small'
          loading={loading}
          columns={itemColumns}
          dataSource={items}
          pagination={false}
          rowKey='cfg_acc_items__id'
        />
      </div>
    )
  }

  return (
    <div className={CONFIG_PANEL_SCROLL_CLASS}>
      <div className='flex items-center justify-between mb-3'>
        <Title level={5} style={{ margin: 0 }}>
          Chart Templates
        </Title>
        <div className='flex items-center gap-2'>
          <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => openEditor(null)}>
            New Template
          </Button>
          <Button size='small' icon={<ReloadOutlined />} onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>

      <Text type='secondary' className='block mb-2'>
        A template is a reusable starting point for a new fund&apos;s chart of accounts. Creating a
        fund from one copies it — later edits to the template do not affect funds already created.
      </Text>

      <Table
        size='small'
        loading={loading}
        columns={listColumns}
        dataSource={templates}
        pagination={false}
        rowKey='cfg_acc_chart_templates__id'
      />
    </div>
  )
}
